import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { AccountSearchSelect } from './AccountSearchSelect';
import { useDispatch, useSelector } from 'react-redux';
import { BudgetLineItemLoadingKeys, clearBudgetLineItemsBySourceAccount, getBudgetLineItemsBySourceAccount } from '../../slices/quickbooks/budgetLineItemSlice';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { arrayHasItems } from '../../helpers/common';
import { calculateByOperator, operatorMap } from '../../consts/quickbooksReports';
import { monthsLowerCase } from '../../consts/forecast';
import { SelectDropdown } from '../SelectDropdown';

const operations = [
    { label: "Multiply (×)", value: 1 },
    { label: "Divide (÷)", value: 2 },
    { label: "Add (+)", value: 3 },
    { label: "Subtract (-)", value: 4 },
];

export const BudgetLineItemFormulaModal = ({
    isOpen,
    onClose,
    onRemove,
    id,
    hasFormula,
    formula,
    onAccept
}) => {

    const dispatch = useDispatch();
    const [selectedOperation, setSelectedOperation] = useState(formula?.operation || 1);
    const [selectedSourceAccount, setSelectedSourceAccount] = useState(formula?.source_account || null);
    const [currentValue, setCurrentValue] = useState(formula?.value || 0);
    const [isPercentageValue, setIsPercentageValue] = useState(formula?.is_percentage || false);
    const [isSeparateValueByMonths, setIsSeparateValueByMonths] = useState(formula?.is_separate_value_by_months || false);
    const [percentageValueByMonths, setPercentageValueByMonths] = useState(formula?.percentage_value_by_months || monthsLowerCase.reduce((acc, m) => { acc[m] = 0; return acc; }, {}));

    const {
        loading: budgetLineItemLoading,
        loadingKeys: budgetLineItemLoadingKeys,
        budgetLineItemsBySourceAccount
    } = useSelector((state) => state.budgetLineItem);

    const [currentSearchParams, setCurrentSearchParams] = useState([]);

    useEffect(() => {
        if (!isOpen) {
            dispatch(clearBudgetLineItemsBySourceAccount({}));
            return;
        }
        dispatch(getBudgetLineItemsBySourceAccount({ id }));
        setCurrentValue(0);
        setIsPercentageValue(false);
        setSelectedSourceAccount(null);
        setSelectedOperation(1);
        if (formula) {
            setSelectedOperation(formula?.operation || 1);
            setSelectedSourceAccount(formula?.source_account || null);
            setCurrentValue(formula?.value || 0);
            setIsPercentageValue(formula?.is_percentage || false);
            setIsSeparateValueByMonths(formula?.is_separate_value_by_months || false);
            setPercentageValueByMonths(formula?.percentage_value_by_months || monthsLowerCase.reduce((acc, m) => { acc[m] = 0; return acc; }, {}));
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    const valueSelectedSourceAccount = 
        arrayHasItems(budgetLineItemsBySourceAccount) ?
        budgetLineItemsBySourceAccount.find((sourceAccount) => sourceAccount.value === selectedSourceAccount) || null
        : null;

    const getSourceAccountData = () => {
        return arrayHasItems(budgetLineItemsBySourceAccount) ? 
            budgetLineItemsBySourceAccount
                .map((sourceAccount) => {
                    return {
                        label: sourceAccount.label,
                        value: sourceAccount.value
                    }
                }) : 
            []
    };

    useEffect(() => {
        const currentSourceAccountData = getSourceAccountData();
        setCurrentSearchParams(currentSourceAccountData);
    }, [budgetLineItemsBySourceAccount]);

    const selectedOperator = operatorMap[selectedOperation];

    const handleAcceptActions = async () => {
        if (!selectedSourceAccount) {
            toast.error(`Empty source account.`, { position: "bottom-right" });
            return;
        }
        await onAccept?.({
            selectedOperation,
            selectedSourceAccount,
            currentValue,
            isPercentageValue,
            isSeparateValueByMonths,
            percentageValueByMonths,
        });
    }

    const getSourceAccountValuesIsLoading = () =>
        budgetLineItemLoading && budgetLineItemLoadingKeys.includes(BudgetLineItemLoadingKeys.getBudgetLineItemsBySourceAccount);

    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh]"
                    onClick={(e) => e.stopPropagation()}
                >
                    {getSourceAccountValuesIsLoading() && <OverlayGrayBackground />}
                    <div className="bg-blue-50 px-6 py-4 border-b border-blue-200 rounded-t-lg">
                        <h2 className="text-xl font-semibold text-blue-900 text-left">Formula Editor</h2>
                        <p className="text-sm text-blue-700 mt-1 text-left">Create a formula for Amazon Shipping Income (applies to entire row)</p>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Source Account
                                </label>
                                <div>
                                    <AccountSearchSelect
                                        options={currentSearchParams}
                                        value={selectedSourceAccount}
                                        onChange={setSelectedSourceAccount}
                                        isLoading={getSourceAccountValuesIsLoading()}
                                        placeholder="Select account to reference"
                                        containerClassName="w-full"
                                    />
                                </div>  
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Operation
                                </label>
                                <div>
                                    <SelectDropdown
                                        key={`${selectedOperation}`}
                                        options={operations}
                                        disabled={isPercentageValue}
                                        value={selectedOperation}
                                        onChange={setSelectedOperation}
                                        itemValueClassname="px-2 py-1 text-sm text-left"
                                        selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                        selectClassname="!bg-white !px-3 !py-2 !border-input"
                                    />
                                </div>  
                            </div>   
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-700 text-left" htmlFor='value'>
                                    Value
                                </label>
                                <div>
                                    <input
                                        id='value'
                                        className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                                        type="number"
                                        min="0"
                                        value={currentValue}
                                        onChange={(e) => {
                                            let rawValue = e.target.value;
                                            let normalizedValue = rawValue.replace(',', '.');
                                            if (/^\d*\.?\d*$/.test(normalizedValue)) {
                                                if (
                                                    normalizedValue.includes('.') &&
                                                    normalizedValue.startsWith('0') &&
                                                    !normalizedValue.startsWith('0.')
                                                ) {
                                                    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                                                } else if (!normalizedValue.includes('.') && normalizedValue.length > 1) {
                                                    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                                                }
                                                setCurrentValue(normalizedValue)
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 mt-5">
                                <div className="flex items-center space-x-2">
                                    <input
                                        id="isPercentage"
                                        type="checkbox"
                                        value={isPercentageValue}
                                        checked={isPercentageValue}
                                        onChange={() => {
                                            setIsPercentageValue(!isPercentageValue)
                                        }}
                                    />
                                    <label className="font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm text-gray-700" htmlFor='isPercentage'>
                                        Value is a percentage
                                    </label>
                                </div> 
                                <div className="flex items-center space-x-2">
                                    <input
                                        id="isSeparateValueByMonths"
                                        type="checkbox"
                                        value={isSeparateValueByMonths}
                                        checked={isSeparateValueByMonths}
                                        onChange={() => {
                                            setIsSeparateValueByMonths(!isSeparateValueByMonths)
                                        }}
                                    />
                                    <label className="font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm text-gray-700" htmlFor='isSeparateValueByMonths'>
                                        Separate value by months
                                    </label>
                                </div> 
                            </div>                  
                        </div>
                        {/* Preview */}
                        {selectedSourceAccount && (
                            <>
                                <div className="bg-gray-50 p-4 rounded-lg flex flex-col items-start gap-2">
                                    <h4 className="font-medium text-gray-900">Formula Preview:</h4>
                                    <p className="text-sm text-gray-700">
                                        {!isPercentageValue ? (
                                            <span>{valueSelectedSourceAccount?.name || ""} {selectedOperator || ""} {currentValue}</span>
                                        ) : (
                                            <span>{valueSelectedSourceAccount?.name || ""} % {currentValue}</span>
                                        )}
                                    </p>
                                    <p className="text-sm font-semibold text-blue-600">
                                        {!isPercentageValue ? (
                                            <span>Example result (January): ${calculateByOperator(valueSelectedSourceAccount?.janValue || 0, Number(currentValue), selectedOperator)}</span>
                                        ) : (
                                            <span>Example result (January): ${Number((valueSelectedSourceAccount?.janValue || 0) * (Number(currentValue) / 100)).toFixed(2)}</span>
                                        )}
                                    </p>
                                </div>
                            </>
                        )}
                        {isSeparateValueByMonths && (
                            <div className="bg-gray-50 p-4 rounded-lg flex flex-col items-start gap-2">
                                <h4 className="font-medium text-gray-900">Separate value by months:</h4>
                                <div className="grid grid-cols-6 gap-2">
                                    {monthsLowerCase.map((month) => (
                                        <div key={month} className="flex flex-col gap-1">
                                            <label className="text-sm font-medium text-gray-700 text-left">
                                                {month} %
                                            </label>
                                            <input
                                                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                                                type="number"
                                                min="0"
                                                value={percentageValueByMonths?.[month] ?? 0}
                                                onChange={(e) => {
                                                    let rawValue = e.target.value;
                                                    let normalizedValue = rawValue.replace(',', '.');
                                                    if (/^\d*\.?\d*$/.test(normalizedValue)) {
                                                        if (
                                                            normalizedValue.includes('.') &&
                                                            normalizedValue.startsWith('0') &&
                                                            !normalizedValue.startsWith('0.')
                                                        ) {
                                                            normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                                                        } else if (!normalizedValue.includes('.') && normalizedValue.length > 1) {
                                                            normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                                                        }
                                                        setPercentageValueByMonths((prev) => ({
                                                            ...(prev || {}),
                                                            [month]: normalizedValue
                                                        }));
                                                    }
                                                }}
                                            />
                                            {selectedSourceAccount && (
                                                <span className="text-xs text-gray-600 text-left">
                                                    {(() => {
                                                        // Separate-by-months preview with two-step logic:
                                                        // 1) If isPercentageValue and currentValue provided -> apply global percent to source.
                                                        //    Else if operator and currentValue provided -> apply operator to source.
                                                        //    Else -> base is source.
                                                        // 2) Apply monthly percentage to the base.
                                                        const sourceMonths = valueSelectedSourceAccount?.months || {};
                                                        const sourceValue = Number(sourceMonths?.[month] || 0);
                                                        const hasCurrentValue = currentValue && Number(currentValue || 0) > 0;
                                                        let intermediate = sourceValue;
                                                        if (isPercentageValue && hasCurrentValue) {
                                                            if (currentValue === "" || currentValue === null || currentValue === undefined) {
                                                                intermediate = 0;
                                                            } else {    
                                                                intermediate = sourceValue * (Number(currentValue || 0) / 100);
                                                            }
                                                        } else if (operatorMap[selectedOperation] && currentValue) {
                                                            const operator = operatorMap[selectedOperation];
                                                            if (!isPercentageValue && hasCurrentValue && operator) {
                                                                if (currentValue === "" || currentValue === null || currentValue === undefined) {
                                                                    intermediate = 0;
                                                                } else {
                                                                    intermediate = calculateByOperator(sourceValue, Number(currentValue || 0), operator);
                                                                }
                                                            }
                                                        } else {
                                                            intermediate = sourceValue;
                                                        }
                                                        const monthPercent = Number(percentageValueByMonths?.[month] || 0);
                                                        const previewValue = intermediate * (monthPercent / 100);
                                                        return `Preview: $${Number(previewValue || 0).toFixed(2)}`;
                                                    })()}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex justify-between pt-4">
                            {hasFormula && (
                                <div>
                                    <button
                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border h-10 px-4 py-2 text-red-600 border-red-200 hover:bg-red-50 bg-transparent"
                                        onClick={async () => {
                                            await onRemove?.({})
                                        }}
                                    >
                                        Remove Formula
                                    </button>
                                </div>
                            )}
                            <div className="flex flex-row gap-2">
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 border h-10 px-4 py-2 hover:bg-gray-100"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                                    onClick={handleAcceptActions}
                                >
                                    Apply Formula
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};